import { keccak256, toUtf8Bytes } from "ethers";

export const CHAIN_ID = 46630;
export const MODEL_ID = "flux2-klein-4b-webgpu-int8";
/**
 * The exact pack a job was rendered with, hashed into the job's on-chain
 * identity so a settlement cannot be about a different model than the one
 * agreed.
 *
 * It used to name a third-party int4 export that declares no licence, which
 * meant the chain recorded the network as running weights nobody had the right
 * to use. It now names ours.
 *
 * Changing this changes MODEL_HASH, and therefore on-chain job identity. That
 * costs nothing in points mode, which is what is deployed and which never
 * touches a chain, but it must happen before any chain deployment rather than
 * after, and any testnet job already in flight under the old hash has to be
 * settled or abandoned first.
 */
export const MODEL_DESCRIPTOR =
  "flux2-klein-4b-onnx-webgpu@530c0c239b4a3d76ddaf7c839b9e99302ce80e7d";
export const MODEL_HASH = keccak256(toUtf8Bytes(`sharemyai:model:${MODEL_DESCRIPTOR}`));
export const REWARD_TOKEN_UNITS_PER_CREDIT = 1_000_000_000_000_000_000n;
// Compatibility alias for coordinator records created before token settlement was introduced.
export const REWARD_WEI_PER_CREDIT = REWARD_TOKEN_UNITS_PER_CREDIT;

export function jobIdToBytes32(jobId) {
  return keccak256(toUtf8Bytes(jobId));
}

export const WORK_ESCROW_ABI = Object.freeze([
  "error AlreadyExists()",
  "error InvalidDeadline()",
  "error InvalidReward()",
  "error InvalidState()",
  "error InsufficientAvailableStake()",
  "error AuditSelectionPending()",
  "error RandomnessPending()",
  "error AuditNotRequired()",
  "error ResolutionWindowOpen()",
  "error ResolutionWindowClosed()",
  "error StakeLocked()",
  "error TokenTransferFailed()",
  "event JobCreated(bytes32 indexed jobId,address indexed consumer,bytes32 indexed modelHash,uint256 reward,uint64 deadline)",
  "event JobClaimed(bytes32 indexed jobId,address indexed worker)",
  "event ResultSubmitted(bytes32 indexed jobId,address indexed worker,bytes32 indexed outputHash)",
  "event JobSettled(bytes32 indexed jobId,address indexed recipient,uint256 amount,bool accepted)",
  "event AuditSelectionFinalized(bytes32 indexed jobId,bool auditRequired,bytes32 entropy)",
  "event AuditedJobResolved(bytes32 indexed jobId,bool accepted,bytes32 indexed evidenceHash,uint256 workerSlash,address challenger)",
  "event OptimisticJobFinalized(bytes32 indexed jobId,address indexed worker)",
  "event SessionUsed(bytes32 indexed grantDigest,address indexed worker,address indexed sessionKey,bytes32 jobId,uint256 nonce,bytes4 action)",
  "function createJob(bytes32 jobId,bytes32 modelHash,uint64 deadline,uint96 reward)",
  "function claimJob(bytes32 jobId,uint64 authorizationDeadline,bytes authorization)",
  "function claimJobWithSession(bytes32 jobId,uint64 authorizationDeadline,bytes authorization,(address worker,address sessionKey,uint64 validAfter,uint64 validUntil,uint32 maxClaims,uint256 epoch) grant,bytes workerAuthorization,bytes sessionSignature)",
  "function submitResult(bytes32 jobId,bytes32 outputHash)",
  "function submitResultWithSession(bytes32 jobId,bytes32 outputHash,(address worker,address sessionKey,uint64 validAfter,uint64 validUntil,uint32 maxClaims,uint256 epoch) grant,bytes workerAuthorization,bytes sessionSignature)",
  "function revokeSession((address worker,address sessionKey,uint64 validAfter,uint64 validUntil,uint32 maxClaims,uint256 epoch) grant)",
  "function revokeAllSessions()",
  "function sessionEpoch(address) view returns (uint256)",
  "function sessionNonces(bytes32) view returns (uint256)",
  "function sessionClaimsUsed(bytes32) view returns (uint32)",
  "function revokedSessionGrants(bytes32) view returns (bool)",
  "function MAX_SESSION_DURATION() view returns (uint64)",
  "function MAX_SESSION_CLAIMS() view returns (uint32)",
  "function sessionGrantDigest((address worker,address sessionKey,uint64 validAfter,uint64 validUntil,uint32 maxClaims,uint256 epoch) grant) view returns (bytes32)",
  "function settleJob(bytes32 jobId,bool accepted)",
  "function resolveAuditedJob(bytes32 jobId,bool accepted,bytes32 evidenceHash)",
  "function finalizeAuditSelection(bytes32 jobId) returns (bool)",
  "function forceAuditAfterRandomnessTimeout(bytes32 jobId) returns (bool)",
  "function finalizeOptimistic(bytes32 jobId)",
  "function refundUnresolvedAudit(bytes32 jobId)",
  "function challengeJob(bytes32 jobId)",
  "function depositStake(uint256 amount)",
  "function requestUnstake(uint128 amount)",
  "function cancelUnstake()",
  "function withdrawStake()",
  "function stakedBalance(address) view returns (uint256)",
  "function lockedExposure(address) view returns (uint256)",
  "function availableStake(address) view returns (uint256)",
  "function requiredExposure(uint256 reward) view returns (uint256)",
  "function unstakeRequests(address) view returns (uint128 amount,uint64 availableAt)",
  "function audits(bytes32 jobId) view returns (uint64 submittedAt,uint64 selectionBlock,uint64 disputeDeadline,uint64 resolutionDeadline,bool selectionFinalized,bool auditRequired,bool challenged,address challenger)",
  "function minimumStake() view returns (uint96)",
  "function challengeBond() view returns (uint96)",
  "function stakeMultiplierBps() view returns (uint32)",
  "function auditRateBps() view returns (uint16)",
  "function disputeWindow() view returns (uint64)",
  "function auditResolutionWindow() view returns (uint64)",
  "function unstakeDelay() view returns (uint64)",
  "function slashTreasury() view returns (address)",
  "function randomnessCoordinator() view returns (address)",
  "function randomnessFulfillmentTimeout() view returns (uint64)",
  "function paused() view returns (bool)",
  "function refundExpired(bytes32 jobId)",
  "function withdraw()",
  "function pendingWithdrawals(address) view returns (uint256)",
  "function owner() view returns (address)",
  "function verifier() view returns (address)",
  "function claimAuthorizer() view returns (address)",
  "function token() view returns (address)",
  "function getJob(bytes32 jobId) view returns ((address consumer,address worker,uint96 reward,uint64 deadline,uint8 status,bytes32 modelHash,bytes32 outputHash))"
]);

export const TEST_TOKEN_ABI = Object.freeze([
  "event FaucetClaimed(address indexed account,uint256 amount)",
  "function name() view returns (string)",
  "function symbol() view returns (string)",
  "function decimals() view returns (uint8)",
  "function FAUCET_AMOUNT() view returns (uint256)",
  "function faucetClaimed(address) view returns (bool)",
  "function faucet() returns (uint256)",
  "function balanceOf(address) view returns (uint256)",
  "function allowance(address,address) view returns (uint256)",
  "function approve(address,uint256) returns (bool)"
]);
